/**
 * ERPMasterScreenFactory — Generates Grid+Form for any simple master
 * Reuses: ERPActionColumn, ERPLookupModal, ERPInput, ERPLookupField, DynamicFieldRenderer, erpToast
 * Pattern: exact copy of CategoryMaster (stable components, portal dropdowns, Enter nav)
 *
 * USAGE:
 *   const BrandMasterScreen = createMasterScreen({ title: 'Brand Master', screenId: '4.3.5', ... });
 *   export default BrandMasterScreen;
 */
import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Download, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, Columns, Save, Grid3X3, XCircle, Minus, FileSpreadsheet, FileText, Globe, RefreshCw } from 'lucide-react';
import ERPActionColumn from './ERPActionColumn';
import ERPAdvancedGrid from './ERPAdvancedGrid';
import DynamicFieldRenderer from './DynamicFieldRenderer';
import { erpToast } from './ERPToast';
import { FormHelp } from '../help-engine/FormHelp';
import { HelpInfo } from '../help-engine/HelpInfo';
import '../../styles/erp-theme.css';
import { API_BASE as API_URL, getToken} from '../../lib/api';


const handleEnterNav = (e) => {
  if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
    e.preventDefault();
    const form = e.target.closest('form');
    if (!form) return;
    const els = Array.from(form.querySelectorAll('input:not([disabled]):not([readonly]),select:not([disabled]),textarea:not([disabled])'));
    const idx = els.indexOf(e.target);
    if (idx >= 0 && idx < els.length - 1) els[idx + 1].focus();
  }
};

const MInput = memo(({ label, value, onChange, required, disabled, testId, type = 'text', fieldId }) => (
  <div className="erp-form-row">
    <label className={`erp-form-label${required ? ' required' : ''}`}>
      {label}
      {fieldId && <HelpInfo controlId={fieldId} controlName={label} />}
    </label>
    <div className="erp-form-control">
      <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} disabled={disabled}
        className="erp-input" data-testid={testId} data-help-control={fieldId} onKeyDown={handleEnterNav} />
    </div>
  </div>
));

const MSelect = memo(({ label, value, onChange, options, disabled, testId, fieldId }) => (
  <div className="erp-form-row">
    <label className="erp-form-label">
      {label}
      {fieldId && <HelpInfo controlId={fieldId} controlName={label} />}
    </label>
    <div className="erp-form-control">
      <select value={value || ''} onChange={(e) => onChange(e.target.value)} disabled={disabled}
        className="erp-input erp-select" data-testid={testId} data-help-control={fieldId} onKeyDown={handleEnterNav}>
        <option value="">--Select--</option>
        {options.map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
      </select>
    </div>
  </div>
));

/**
 * @param {Object} config
 * @param {string} config.title - Screen title
 * @param {string} config.screenId - e.g. '4.3.5'
 * @param {string} config.module - e.g. 'Procurement'
 * @param {string} config.apiPath - e.g. 'brand' → /api/masters/brand
 * @param {string} config.apiBase - e.g. '/api/pos' (default: '/api/masters')
 * @param {string} config.screenName - e.g. 'BrandMaster' (for dynamic fields)
 * @param {string} config.idField - e.g. 'BrandId'
 * @param {string} config.nameField - e.g. 'BrandName'
 * @param {Array} config.gridColumns - [{field, header, width?}]
 * @param {Array} config.formFields - [{field, label, type?, required?, disabled?, options?}]
 * @param {Array} config.tabs - [{id, label}] optional
 * @param {Function} config.formFieldsForTab - (tabId) => formFields[] optional
 */
export const createMasterScreen = (config) => {
  const MasterScreen = ({ theme = 'light', onBack }) => {
    const [view, setView] = useState('grid');
    const [editData, setEditData] = useState(null);
    const [formMode, setFormMode] = useState('add');
    const [token, setToken] = useState('');
    useEffect(() => { setToken(getToken() || ''); }, []);

    if (view === 'form') return <MasterForm config={config} token={token} editData={editData} mode={formMode} onGridView={() => { setView('grid'); setEditData(null); }} onSaved={() => setView('grid')} onBack={onBack} />;
    return <MasterGrid config={config} token={token} onAddNew={() => { setEditData(null); setFormMode('add'); setView('form'); }} onEdit={(row, mode) => { setEditData(row); setFormMode(mode || 'edit'); setView('form'); }} onBack={onBack} />;
  };

  return MasterScreen;
};

// ── GRID (delegates to ERPAdvancedGrid) ──
const MasterGrid = ({ config, token, onAddNew, onEdit, onBack }) => {
  const { title, apiPath, idField, nameField, gridColumns } = config;
  const apiBase = (config.apiBase || 'api/masters').replace(/^\//, '');
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (sortField) { params.set('sortField', sortField); params.set('sortDir', sortDir); }
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(`filter${k}`, v); });
      const res = await fetch(`${API_URL}/${apiBase}/${apiPath}?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (json.success) { setData(json.data || []); setTotal(json.total || 0); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, pageSize, sortField, sortDir, filters, token, apiPath, apiBase]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSort = (f) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
    setPage(1);
  };

  const handleVoid = async (row) => {
    if (!window.confirm(`Void "${row[nameField]}"?`)) return;
    await fetch(`${API_URL}/${apiBase}/${apiPath}/${row[idField]}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    loadData();
  };

  return (
    <ERPAdvancedGrid
      title={title}
      columns={gridColumns}
      data={data}
      total={total}
      idField={idField}
      nameField={nameField}
      page={page}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={setPageSize}
      sortField={sortField}
      sortDir={sortDir}
      onSort={handleSort}
      filters={filters}
      onFilterChange={(f) => { setFilters(f); setPage(1); }}
      loading={loading}
      onAddNew={onAddNew}
      onEdit={onEdit}
      onVoid={handleVoid}
      testId={`${apiPath}-master`}
      exportFileName={title?.replace(/ /g, '_')}
      onBack={onBack}
    />
  );
};

// ── FORM ──
const MasterForm = ({ config, token, editData, mode: initialMode, onGridView, onSaved, onBack }) => {
  const { title, apiPath, screenName, idField, nameField, formFields, tabs, formFieldsForTab } = config;
  const apiBase = (config.apiBase || 'api/masters').replace(/^\//, '');
  const [currentMode, setCurrentMode] = useState(initialMode);
  const isView = currentMode === 'view';
  const userRole = (() => { try { return JSON.parse(localStorage.getItem('retailcliq_user') || '{}').role || ''; } catch { return ''; } })();
  const isSuperAdmin = userRole === 'SUPER_ADMIN' || userRole === 'admin' || userRole === 'ADMINSUPPORT' || userRole === 'LICENSOR';

  // Sync with parent mode changes
  useEffect(() => { setCurrentMode(initialMode); }, [initialMode]);

  const emptyForm = {};
  (formFields || []).forEach(f => { emptyForm[f.field] = f.default || ''; });
  const [form, setForm] = useState(emptyForm);
  const [activeTab, setActiveTab] = useState(tabs?.[0]?.id || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [recentData, setRecentData] = useState([]);
  const [dynValues, setDynValues] = useState({});

  useEffect(() => { if (editData && currentMode !== 'add') setForm({ ...emptyForm, ...editData }); }, [editData, currentMode]);

  const loadRecent = useCallback(async () => {
    if (!token) return;
    try { const r = await fetch(`${API_URL}/${apiBase}/${apiPath}?page=1&pageSize=10`, { headers: { Authorization: `Bearer ${token}` } }); const j = await r.json(); if (j.success) setRecentData(j.data || []); } catch {}
  }, [token, apiPath]);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  const set = (n, v) => setForm(p => ({ ...p, [n]: v }));

  const handleSave = async () => {
    const reqField = (formFields || []).find(f => f.required && !form[f.field]?.toString().trim());
    if (reqField) { setError(`${reqField.label} is required`); return; }
    setSaving(true); setError('');
    try {
      const method = currentMode === 'edit' ? 'PUT' : 'POST';
      const url = currentMode === 'edit' ? `${API_URL}/${apiBase}/${apiPath}/${form[idField]}` : `${API_URL}/${apiBase}/${apiPath}`;
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(form) });
      const json = await res.json();
      if (json.success) {
        const recId = json.data?.[idField] || form[idField];
        if (recId && Object.keys(dynValues).length > 0) {
          await fetch(`${API_URL}/api/dynamic-fields/values`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ screen: screenName, recordId: recId, values: dynValues }) });
        }
        erpToast.success('Saved successfully!');
        setForm(emptyForm); setDynValues({}); setCurrentMode('add'); loadRecent(); onSaved?.();
      } else setError(json.error || 'Save failed');
    } catch { setError('Save failed'); }
    setSaving(false);
  };

  const handleClear = () => { setForm(emptyForm); setDynValues({}); setError(''); setCurrentMode('add'); };

  const renderFields = (fields) => fields.map(f => {
    if (f.type === 'select') return <MSelect key={f.field} label={f.label} value={form[f.field]} onChange={(v) => set(f.field, v)} options={f.options || []} disabled={isView || f.disabled} testId={`input-${f.field}`} fieldId={f.field} />;
    return <MInput key={f.field} label={f.label} value={form[f.field]} onChange={(v) => set(f.field, v)} required={f.required} disabled={isView || f.disabled} testId={`input-${f.field}`} type={f.type || 'text'} fieldId={f.field} />;
  });

  const currentFields = tabs && formFieldsForTab ? formFieldsForTab(activeTab) : formFields;

  return (
    <FormHelp formName={screenName} userRole={userRole}>
    <div className="erp-module" data-testid={`${apiPath}-master-form`} data-dev-component={screenName} data-dev-file={`screens/${(config.module||'').toLowerCase()}/masters/${screenName}.js`}>
      <div className="erp-screen-header">
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          {onBack && (
            <button onClick={onBack} data-testid="screen-back-btn"
              className="erp-btn erp-btn-ghost" style={{padding:'3px 8px',fontSize:11,gap:4}}>
              <ChevronLeft size={14} /> BACK
            </button>
          )}
          <span className="erp-screen-title">{title}</span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:16,fontSize:12}}>
          {form[idField] ? <span style={{color:'var(--erp-text-secondary)'}}>ID: <strong style={{fontFamily:'var(--erp-font-mono)',color:'var(--erp-primary)'}}>{form[idField]}</strong></span>
          : <><span style={{color:'var(--erp-text-muted)'}}>Previous ID: <strong style={{fontFamily:'var(--erp-font-mono)'}}>{recentData[0]?.[idField] || '—'}</strong></span><span style={{color:'var(--erp-text-secondary)'}}>New ID: <strong style={{color:'var(--erp-warning)',fontFamily:'var(--erp-font-mono)'}}>Auto-generated</strong></span></>}
        </div>
        <div className="erp-screen-actions">
          {!isView && <button className="erp-btn erp-btn-ghost" onClick={handleSave} disabled={saving}>{currentMode === 'edit' ? <RefreshCw size={14} /> : <Save size={14} />} {saving ? 'Saving...' : (currentMode === 'edit' ? 'Update' : 'Save')}</button>}
          <button className="erp-btn erp-btn-ghost" onClick={onGridView}><Grid3X3 size={14} /> Gridview</button>
          <button className="erp-btn erp-btn-ghost" onClick={handleClear}><XCircle size={14} /> Clear</button>
          {isSuperAdmin && !isView && <button className="erp-btn erp-btn-primary" onClick={() => { const el = document.querySelector('[data-testid="add-dynamic-field-btn"]'); if (el) el.click(); else document.querySelector('[data-testid="dynamic-fields-section"]')?.scrollIntoView({behavior:'smooth'}); }}><Plus size={14} /> Add Field</button>}
        </div>
      </div>
      {error && <div className="erp-alert-error">{error}</div>}
      <form style={{flex:1,overflow:'auto',padding:'var(--erp-sp-4)'}} onSubmit={(e) => e.preventDefault()}>
        <div className="erp-form-card">
          {tabs && (<div className="erp-tabs">{tabs.map(t => <button key={t.id} className={`erp-tab${activeTab === t.id ? ' active' : ''}`} onClick={() => setActiveTab(t.id)}>{t.label}</button>)}</div>)}
          <div className="erp-form-section">
            <div className="erp-form-2col">{renderFields(currentFields)}</div>
          </div>
        </div>
        <DynamicFieldRenderer screenName={screenName} recordId={form[idField]} token={token} values={dynValues} onChange={(k, v) => setDynValues(p => ({...p,[k]:v}))} readOnly={isView} userRole={userRole} />
        <div className="erp-embedded-grid">
          <div className="erp-grid-wrapper">
            <table className="erp-grid erp-grid-header-fixed">
              <thead><tr><th style={{width:50}}>Actions</th>{config.gridColumns.slice(0,4).map(c => <th key={c.field}>{c.header}</th>)}</tr></thead>
            </table>
            <div className="erp-grid-body-scroll">
              <table className="erp-grid">
                <tbody>{recentData.map(row => (
                  <tr key={row[idField]}>
                    <td style={{width:50}}><ERPActionColumn row={row}
                      onView={() => { setForm({...emptyForm,...row}); setCurrentMode('view'); }}
                      onEdit={() => { setForm({...emptyForm,...row}); setCurrentMode('edit'); }}
                      onVoid={async () => {
                        if (!window.confirm(`Void "${row[nameField]}"?`)) return;
                        try {
                          await fetch(`${API_URL}/${apiBase}/${apiPath}/${row[idField]}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
                          erpToast.success(`Voided: ${row[idField]}`);
                          loadRecent();
                        } catch { erpToast.error('Void failed'); }
                      }}
                      onPreview={() => erpToast.info(`Preview: ${row[idField]}`)}
                      onPrint={() => window.print()}
                      onExport={() => { const t = Object.entries(row).map(([k,v])=>`${k}: ${v}`).join('\n'); const a=document.createElement('a'); a.href=URL.createObjectURL(new Blob([t])); a.download=`${row[idField]}.txt`; a.click(); }}
                    /></td>
                    {config.gridColumns.slice(0,4).map(c => <td key={c.field} className={c.field === idField ? 'cell-mono' : c.field === nameField ? 'cell-bold' : ''}>{row[c.field]||''}</td>)}
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        </div>
      </form>
    </div>
    </FormHelp>
  );
};

export default createMasterScreen;
